import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

export const ATTACK_METHODS = {
  bot: [
    {
      id: 'null_handshake',
      title: 'TCP Null Handshake Flood',
      subRu: 'Зависание сокетов в состоянии ожидания завершения авторизации',
      subEn: 'Holding sockets in waiting state for auth completion',
      rec: true
    },
    {
      id: 'bot_join_spam',
      title: 'Botnet Join & Ping Spam',
      subRu: 'Массовое подключение эмулированных клиентов в очередь ядра',
      subEn: 'Mass emulated client connection flooding kernel queue',
      rec: false
    },
    {
      id: 'keep_alive_exhaust',
      title: 'Keep-Alive Buffer Exhaustion',
      subRu: 'Удержание открытых буферов Netty до исчерпания пула сокетов',
      subEn: 'Holding Netty open buffers until socket pool exhaustion',
      rec: false
    },
    {
      id: 'chunk_stress',
      title: 'Chunk Request Overload',
      subRu: 'Постоянный запрос генерации и синхронизации чанков мира L7',
      subEn: 'Continuous world chunk generation & sync requests at L7',
      rec: false
    }
  ],
  packet: [
    {
      id: 'tcp_syn_flood',
      title: 'TCP SYN / ACK Flood (L4)',
      subRu: 'Заваливание таблицы полуоткрытых соединений стека ядра ОС',
      subEn: 'Overwhelming half-open connection tables in OS kernel stack',
      rec: true
    },
    {
      id: 'udp_malformed',
      title: 'UDP High-Rate Malformed Stream',
      subRu: 'Бомбардировка сетевого интерфейса аномальными датаграммами',
      subEn: 'Bombarding network interface with abnormal datagrams',
      rec: false
    },
    {
      id: 'query_saturation',
      title: 'Minecraft Query / Ping Saturation',
      subRu: 'Предельная загрузка обработчика статуса и пинга сервера',
      subEn: 'Maximum saturation of server status and ping handler',
      rec: false
    },
    {
      id: 'corrupted_handshake',
      title: 'Corrupted Handshake Buffer Overrun',
      subRu: 'Отправка поврежденных заголовков для сбоя парсера пакетов Netty',
      subEn: 'Sending corrupted headers to trigger Netty packet parser crash',
      rec: false
    }
  ]
};

const DdosConfigCard = ({
  attackType = 'bot',
  selectedMethod,
  onSelectMethod,
  threads = 100,
  onThreadsChange,
  duration = 60,
  onDurationChange,
  proxyEnabled = true,
  onProxyToggle,
  randomizeEnabled = true,
  onRandomizeToggle,
  failSafeEnabled = true,
  onFailSafeToggle,
  isRunning = false,
  isLoading = false,
  isDisabled = false,
  onStart,
  onStop
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const methodsList = ATTACK_METHODS[attackType] || ATTACK_METHODS.bot;
  const currentMethod = methodsList.find((m) => m.id === selectedMethod) || methodsList[0];

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMethodSelect = (method) => {
    onSelectMethod?.(method.id);
    setIsDropdownOpen(false);
  };

  const handleActionClick = () => {
    if (isRunning) {
      onStop?.();
    } else {
      onStart?.();
    }
  };

  const durationOptions = [
    { value: 30, labelRu: '30 сек', labelEn: '30 sec' },
    { value: 60, labelRu: '60 сек', labelEn: '60 sec' },
    { value: 120, labelRu: '120 сек', labelEn: '120 sec' },
    { value: 300, labelRu: '300 сек', labelEn: '300 sec' },
    { value: 0, labelRu: 'Бессрочно', labelEn: 'Unlimited' }
  ];

  const threadPills = [50, 100, 250, 500, 1000];

  return (
    <div className="config-card">
      <div className="card-heading-row">
        <div className="card-title-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="21" x2="4" y2="14" />
            <line x1="4" y1="10" x2="4" y2="3" />
            <line x1="12" y1="21" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12" y2="3" />
            <line x1="20" y1="21" x2="20" y2="16" />
            <line x1="20" y1="12" x2="20" y2="3" />
            <line x1="1" y1="14" x2="7" y2="14" />
            <line x1="9" y1="8" x2="15" y2="8" />
            <line x1="17" y1="16" x2="23" y2="16" />
          </svg>
          <h3 className="card-title">
            {isEn ? 'Attack Configuration' : 'Конфигурация атаки'}
          </h3>
        </div>
      </div>

      {/* 1. Выбор метода */}
      <div className="field-group">
        <div className="field-label-row">
          <label className="field-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            <span>{isEn ? 'Method' : 'Метод'}</span>
          </label>
        </div>

        <div className={`custom-dropdown-wrap ${isDropdownOpen ? 'open' : ''}`} ref={dropdownRef}>
          <button
            type="button"
            className="custom-dropdown-trigger"
            onClick={() => !isRunning && setIsDropdownOpen((prev) => !prev)}
            disabled={isRunning}
          >
            <div className="dropdown-trigger-content">
              <span className="dropdown-selected-name">{currentMethod.title}</span>
              {currentMethod.rec && (
                <span className="dropdown-badge-rec">
                  {isEn ? 'Recommended' : 'Рекомендовано'}
                </span>
              )}
            </div>
            <svg className="dropdown-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          <div className="custom-dropdown-menu">
            {methodsList.map((m) => {
              const isSelected = m.id === currentMethod.id;
              return (
                <div
                  key={m.id}
                  className={`dropdown-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleMethodSelect(m)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleMethodSelect(m); }}
                >
                  <div className="dropdown-item-info">
                    <div className="dropdown-item-title-row">
                      <span className="dropdown-item-title">{m.title}</span>
                      {m.rec && (
                        <span className="dropdown-badge-rec">
                          {isEn ? 'Recommended' : 'Рекомендовано'}
                        </span>
                      )}
                    </div>
                    <span className="dropdown-item-sub">
                      {isEn ? m.subEn : m.subRu}
                    </span>
                  </div>
                  <span className="dropdown-check-icon">
                    {isSelected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 2. Количество потоков */}
      <div className="field-group">
        <div className="field-label-row">
          <label className="field-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="2" width="20" height="8" rx="2" />
              <rect x="2" y="14" width="20" height="8" rx="2" />
              <line x1="6" y1="6" x2="6.01" y2="6" />
              <line x1="6" y1="18" x2="6.01" y2="18" />
            </svg>
            <span>{isEn ? 'Thread Count' : 'Количество потоков'}</span>
          </label>
          <span className="field-plain-val">
            {threads} {isEn ? 'threads' : 'потоков'}
          </span>
        </div>

        <input
          type="range"
          min="10"
          max="1000"
          value={threads}
          step="10"
          className="threads-range-slider"
          disabled={isRunning}
          onChange={(e) => onThreadsChange?.(parseInt(e.target.value, 10))}
        />

        <div className="quick-pills-row">
          {threadPills.map((val) => (
            <button
              key={val}
              type="button"
              className={`quick-pill-btn ${threads === val ? 'active' : ''}`}
              onClick={() => !isRunning && onThreadsChange?.(val)}
              disabled={isRunning}
            >
              {val}
            </button>
          ))}
        </div>
      </div>

      {/* 3. Длительность теста */}
      <div className="field-group">
        <div className="field-label-row">
          <label className="field-label">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>{isEn ? 'Duration' : 'Время'}</span>
          </label>
        </div>

        <div className="quick-pills-row duration-pills-row">
          {durationOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`quick-pill-btn ${duration === opt.value ? 'active' : ''}`}
              onClick={() => !isRunning && onDurationChange?.(opt.value)}
              disabled={isRunning}
            >
              {isEn ? opt.labelEn : opt.labelRu}
            </button>
          ))}
        </div>
      </div>

      {/* Apple переключатели */}
      <div className="toggles-group">
        <label className="toggle-row">
          <div className="toggle-label-wrap">
            <span className="toggle-title">
              {isEn ? 'Proxy server rotation' : 'Ротация прокси-серверов'}
            </span>
            <span className="toggle-desc">
              {isEn ? 'SOCKS5 pool with automatic outgoing node obfuscation' : 'SOCKS5 пул с автоматической маскировкой исходящих узлов'}
            </span>
          </div>
          <div className="apple-switch">
            <input
              type="checkbox"
              checked={proxyEnabled}
              disabled={isRunning}
              onChange={(e) => onProxyToggle?.(e.target.checked)}
            />
            <span className="switch-slider" />
          </div>
        </label>

        <label className="toggle-row">
          <div className="toggle-label-wrap">
            <span className="toggle-title">
              {isEn ? 'Randomize headers & payload' : 'Рандомизация заголовков и payload'}
            </span>
            <span className="toggle-desc">
              {isEn ? 'Varying packet size to bypass simple L4/L7 heuristics' : 'Варьирование размеров пакетов для обхода простых сигнатур L4/L7'}
            </span>
          </div>
          <div className="apple-switch">
            <input
              type="checkbox"
              checked={randomizeEnabled}
              disabled={isRunning}
              onChange={(e) => onRandomizeToggle?.(e.target.checked)}
            />
            <span className="switch-slider" />
          </div>
        </label>

        <label className="toggle-row">
          <div className="toggle-label-wrap">
            <span className="toggle-title">
              {isEn ? 'Fail-Safe fuse' : 'Fail-Safe предохранитель'}
            </span>
            <span className="toggle-desc">
              {isEn ? 'Auto-stop stress test if latency threshold RTT > 1000 ms' : 'Автостоп тестирования при превышении порога RTT > 1000 ms'}
            </span>
          </div>
          <div className="apple-switch">
            <input
              type="checkbox"
              checked={failSafeEnabled}
              disabled={isRunning}
              onChange={(e) => onFailSafeToggle?.(e.target.checked)}
            />
            <span className="switch-slider" />
          </div>
        </label>
      </div>

      {/* Главная кнопка старт / стоп */}
      <button
        type="button"
        className={`action-btn-main ${isRunning ? 'running' : ''}`}
        onClick={handleActionClick}
        disabled={isDisabled || isLoading}
      >
        {isLoading ? (
          <span className="btn-spinner-icon" />
        ) : isRunning ? (
          <>
            <span className="btn-spinner-icon" />
            <span>{isEn ? 'Stop Attack' : 'Остановить атаку'}</span>
          </>
        ) : (
          <>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            <span>{isEn ? 'Start Stress Test' : 'Запустить стресс-тест'}</span>
          </>
        )}
      </button>
    </div>
  );
};

export default DdosConfigCard;
